/**
 * lot_envelope.ts — the BUILDABLE ENVELOPE of a lot (2026-09-23, "organise
 * the towns: buildings scaled right, off the roads, off each other").
 *
 * The engine's blocks are the faces of the road graph — bounded by road
 * CENTRELINES, never inset by a road's width — and `BuildingPlacer` seats a
 * pad `minSetback` (2 m) from the parcel's frontage edge. So every pad's front
 * wall stood 2 m from the middle of its street, inside an 8 m half-width
 * arterial, and the assembly then scaled the building to the tier's lattice
 * width around that centre: 57 % of the world's buildings reached into a
 * carriageway, 36 % had the road's own centreline running through them.
 *
 * `lotEnvelope` answers the question the planner never asked: the largest
 * rectangle, facing the frontage, that lies inside the parcel AND keeps every
 * road's `clear` (half-width + sidewalk + setback) from that road's centreline
 * and `sideGap` from every lot line — the box a building may fill. The pad
 * becomes that box; the assembly scales the model INTO it (never the tier's
 * lattice); two lots never share ground, so two buildings never meet.
 *
 * Pure, no THREE: the finalizer (main thread, workers, bakers) and the layout
 * audit call it. `parcelEnvelope` is the same search with no frontage — the
 * park a landmark stands in.
 */

export interface LotRoad {
  polyline: ArrayLike<number>;
  /** The inset a building keeps from this road's centreline (half-width + sidewalk + setback). */
  clear: number;
}
export interface LotEnvelope { cx: number; cz: number; w: number; d: number; yaw: number }

/** Distance from a point to a segment. */
export function segDist(px: number, pz: number, ax: number, az: number, bx: number, bz: number): number {
  const vx = bx - ax, vz = bz - az, L2 = vx * vx + vz * vz;
  const t = L2 > 0 ? Math.max(0, Math.min(1, ((px - ax) * vx + (pz - az) * vz) / L2)) : 0;
  return Math.hypot(px - (ax + vx * t), pz - (az + vz * t));
}
/** Distance from a segment to the axis-aligned rectangle [s0,s1]×[t0,t1] (0 when they meet). */
function segRectDist(ax: number, at: number, bx: number, bt: number, s0: number, s1: number, t0: number, t1: number): number {
  // Slab (Liang–Barsky) clip: any part of the segment inside the rect → 0.
  let lo = 0, hi = 1;
  const dx = bx - ax, dt = bt - at;
  const slab = (p: number, q: number): boolean => {
    if (p === 0) return q >= 0;
    const r = q / p;
    if (p < 0) { if (r > hi) return false; if (r > lo) lo = r; } else { if (r < lo) return false; if (r < hi) hi = r; }
    return true;
  };
  if (slab(-dx, ax - s0) && slab(dx, s1 - ax) && slab(-dt, at - t0) && slab(dt, t1 - at) && lo <= hi) return 0;
  // Apart: the nearest pair is a rect corner to the segment or a segment end to the rect.
  let best = Infinity;
  for (const [cs, ct] of [[s0, t0], [s1, t0], [s1, t1], [s0, t1]]) { const d = segDist(cs, ct, ax, at, bx, bt); if (d < best) best = d; }
  for (const [px, pt] of [[ax, at], [bx, bt]]) {
    const ds = Math.max(s0 - px, 0, px - s1), dtt = Math.max(t0 - pt, 0, pt - t1);
    const d = Math.hypot(ds, dtt); if (d < best) best = d;
  }
  return best;
}
/** Distance from a polyline to the rectangle (0 when they meet). */
function polylineRectDist(p: ArrayLike<number>, s0: number, s1: number, t0: number, t1: number): number {
  let best = Infinity;
  for (let i = 0; i + 3 < p.length; i += 2) { const d = segRectDist(p[i], p[i + 1], p[i + 2], p[i + 3], s0, s1, t0, t1); if (d < best) best = d; if (best === 0) break; }
  return best;
}
function polylineDist(px: number, pz: number, p: ArrayLike<number>): number {
  let best = Infinity;
  for (let i = 0; i + 3 < p.length; i += 2) { const d = segDist(px, pz, p[i], p[i + 1], p[i + 2], p[i + 3]); if (d < best) best = d; }
  return best;
}
function pointInPoly(px: number, pz: number, poly: ArrayLike<number>): boolean {
  let inside = false;
  const n = poly.length / 2;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = poly[i * 2], zi = poly[i * 2 + 1], xj = poly[j * 2], zj = poly[j * 2 + 1];
    if ((zi > pz) !== (zj > pz) && px < ((xj - xi) * (pz - zi)) / (zj - zi) + xi) inside = !inside;
  }
  return inside;
}

/** The clearance each parcel edge demands: a road's `clear` where the edge runs along a road, else `sideGap`. */
export function edgeClearances(parcel: ArrayLike<number>, roads: readonly LotRoad[], sideGap: number): Float64Array {
  const n = parcel.length / 2;
  const out = new Float64Array(n).fill(sideGap);
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const ax = parcel[i * 2], az = parcel[i * 2 + 1], bx = parcel[j * 2], bz = parcel[j * 2 + 1];
    const mx = (ax + bx) / 2, mz = (az + bz) / 2;
    for (const r of roads) {
      // An edge lies ON a road when its midpoint and both ends sit on the centreline (a block face is cut from it).
      if (polylineDist(mx, mz, r.polyline) > 1.2 || polylineDist(ax, az, r.polyline) > 2.0 || polylineDist(bx, bz, r.polyline) > 2.0) continue;
      if (r.clear > out[i]) out[i] = r.clear;
    }
  }
  return out;
}

/** A road's polyline in the parcel's frame, with the inset a building keeps from its centreline. */
interface FrameRoad { pts: Float64Array; clear: number }

/**
 * The roads that can reach a polygon (world coords): those whose polyline bbox
 * comes within `clear` of the polygon's, re-expressed in the frame (origin +
 * the unit axes u along the frontage, n inward).
 */
function frameRoads(parcel: ArrayLike<number>, roads: readonly LotRoad[], ox: number, oz: number, ux: number, uz: number, nx: number, nz: number): FrameRoad[] {
  let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity;
  for (let k = 0; k + 1 < parcel.length; k += 2) { const x = parcel[k], z = parcel[k + 1]; if (x < x0) x0 = x; if (x > x1) x1 = x; if (z < z0) z0 = z; if (z > z1) z1 = z; }
  const out: FrameRoad[] = [];
  for (const r of roads) {
    const p = r.polyline;
    let rx0 = Infinity, rx1 = -Infinity, rz0 = Infinity, rz1 = -Infinity;
    for (let k = 0; k + 1 < p.length; k += 2) { const x = p[k], z = p[k + 1]; if (x < rx0) rx0 = x; if (x > rx1) rx1 = x; if (z < rz0) rz0 = z; if (z > rz1) rz1 = z; }
    const c = r.clear + 0.5;
    if (rx1 < x0 - c || rx0 > x1 + c || rz1 < z0 - c || rz0 > z1 + c) continue;
    const pts = new Float64Array(p.length);
    for (let k = 0; k + 1 < p.length; k += 2) { const dx = p[k] - ox, dz = p[k + 1] - oz; pts[k] = dx * ux + dz * uz; pts[k + 1] = dx * nx + dz * nz; }
    out.push({ pts, clear: r.clear });
  }
  return out;
}

/** Signed area of a ring (frame coords): the sign is the winding. */
function signedArea(p: ArrayLike<number>): number {
  let a = 0; const n = p.length / 2;
  for (let i = 0; i < n; i++) { const j = (i + 1) % n; a += p[i * 2] * p[j * 2 + 1] - p[j * 2] * p[i * 2 + 1]; }
  return a / 2;
}
/** Sutherland–Hodgman: the part of `ring` where (p − o)·n ≥ 0. */
function clipHalfPlane(ring: number[], ox: number, ot: number, nx: number, nt: number): number[] {
  const out: number[] = []; const n = ring.length / 2;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const ax = ring[i * 2], at = ring[i * 2 + 1], bx = ring[j * 2], bt = ring[j * 2 + 1];
    const da = (ax - ox) * nx + (at - ot) * nt, db = (bx - ox) * nx + (bt - ot) * nt;
    if (da >= 0) out.push(ax, at);
    if ((da >= 0) !== (db >= 0)) { const f = da / (da - db); out.push(ax + (bx - ax) * f, at + (bt - at) * f); }
  }
  return out;
}
function segSegCross(ax: number, at: number, bx: number, bt: number, cx: number, ct: number, dx: number, dt: number): boolean {
  const o = (px: number, pt: number, qx: number, qt: number, rx: number, rt: number): number => (qx - px) * (rt - pt) - (qt - pt) * (rx - px);
  const o1 = o(ax, at, bx, bt, cx, ct), o2 = o(ax, at, bx, bt, dx, dt), o3 = o(cx, ct, dx, dt, ax, at), o4 = o(cx, ct, dx, dt, bx, bt);
  return (o1 > 0) !== (o2 > 0) && (o3 > 0) !== (o4 > 0);
}
function segMeetsRing(ring: number[], ax: number, at: number, bx: number, bt: number): boolean {
  if (pointInPoly(ax, at, ring) || pointInPoly(bx, bt, ring)) return true;
  const n = ring.length / 2;
  for (let i = 0; i < n; i++) { const j = (i + 1) % n; if (segSegCross(ax, at, bx, bt, ring[i * 2], ring[i * 2 + 1], ring[j * 2], ring[j * 2 + 1])) return true; }
  return false;
}
/** The s-interval of a ring's chord at height t that holds `sRef` (else its widest), or null. */
function chordAt(ring: number[], t: number, sRef: number): [number, number] | null {
  const xs: number[] = []; const n = ring.length / 2;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n; const at = ring[i * 2 + 1], bt = ring[j * 2 + 1];
    if ((at > t) === (bt > t)) continue;
    const f = (t - at) / (bt - at); xs.push(ring[i * 2] + (ring[j * 2] - ring[i * 2]) * f);
  }
  if (xs.length < 2) return null;
  xs.sort((a, b) => a - b);
  let best: [number, number] | null = null;
  for (let k = 0; k + 1 < xs.length; k += 2) {
    const lo = xs[k], hi = xs[k + 1];
    if (sRef >= lo && sRef <= hi) return [lo, hi];
    if (!best || hi - lo > best[1] - best[0]) best = [lo, hi];
  }
  return best;
}

/**
 * The largest rectangle [s0,s1]×[t0,t1] in a frame whose polygon `poly` (frame
 * coords), per-edge clearances AND the roads themselves allow.
 *
 * The parcel is INSET by every edge's clearance (half-plane clips), and every
 * road segment that still reaches into the inset ring (an alley through a
 * block, a road along a mis-classified side) clips it once more on the
 * frontage's side; the candidates are then the chords of that ring on a
 * 0.5 m ladder of depths — a coarse fraction grid missed the 6.4 m of room
 * between its rungs on half the world's lots. Every candidate is still
 * VALIDATED (`fits`: the polygon, the edge clearances and the exact
 * segment-vs-rectangle road test), because a concave parcel or a road cutting
 * a corner is not captured by chords. Anchored to a frontage, the rectangle
 * nearest the street among those within 80 % of the best area wins.
 */
/** Why the last `bestRect` gave nothing (diagnostics for the layout audit). */
export const lotFitWhy = { why: '', ring0: 0, ring1: 0, cands: 0, tMin: 0, tMax: 0, tFront: 0, clips: [] as string[], ring: [] as number[] };
function bestRect(poly: Float64Array, clear: Float64Array, tFront: number, minSide: number, anchoredFront: boolean, roads: readonly FrameRoad[], sRef: number): { s0: number; s1: number; t0: number; t1: number } | null {
  const n = poly.length / 2;
  const W = lotFitWhy; W.why = ''; W.ring0 = 0; W.ring1 = 0; W.cands = 0; W.tFront = tFront; W.clips = []; W.ring = [];
  const fits = (s0: number, s1: number, t0: number, t1: number): boolean => {
    const pts = [[s0, t0], [s1, t0], [s1, t1], [s0, t1], [(s0 + s1) / 2, t0], [(s0 + s1) / 2, t1], [s0, (t0 + t1) / 2], [s1, (t0 + t1) / 2]];
    for (const [ps, pt] of pts) {
      if (!pointInPoly(ps, pt, poly)) { W.why = `poly (${ps.toFixed(1)},${pt.toFixed(1)})`; return false; }
      for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        const d = segDist(ps, pt, poly[i * 2], poly[i * 2 + 1], poly[j * 2], poly[j * 2 + 1]);
        if (d < clear[i] - 1e-6) { W.why = `edge ${i} d ${d.toFixed(2)} < ${clear[i]}`; return false; }
      }
    }
    // The roads: the WHOLE rectangle keeps each road's clearance — a point
    // sample missed a local road crossing a deep lot between its samples
    // (46 pad/road pairs in one district, the road through the middle).
    for (const r of roads) { const d = polylineRectDist(r.pts, s0, s1, t0, t1); if (d < r.clear - 1e-6) { W.why = `road d ${d.toFixed(2)} < ${r.clear}`; return false; } }
    return true;
  };
  // The inset ring.
  const ccw = signedArea(poly) > 0;
  let ring: number[] = Array.from(poly);
  for (let i = 0; i < n && ring.length >= 6; i++) {
    const j = (i + 1) % n;
    const ax = poly[i * 2], at = poly[i * 2 + 1], bx = poly[j * 2], bt = poly[j * 2 + 1];
    const L = Math.hypot(bx - ax, bt - at); if (L < 1e-9) continue;
    const nx = (ccw ? -(bt - at) : (bt - at)) / L, nt = (ccw ? (bx - ax) : -(bx - ax)) / L;
    ring = clipHalfPlane(ring, ax + nx * clear[i], at + nt * clear[i], nx, nt);
  }
  W.ring0 = ring.length / 2;
  if (ring.length < 6) { W.why = 'inset empties the parcel'; return null; }
  let rs = 0, rt = 0; for (let k = 0; k + 1 < ring.length; k += 2) { rs += ring[k]; rt += ring[k + 1]; } rs /= ring.length / 2; rt /= ring.length / 2;
  for (const r of roads) {
    const p = r.pts;
    for (let i = 0; i + 3 < p.length && ring.length >= 6; i += 2) {
      if (!segMeetsRing(ring, p[i], p[i + 1], p[i + 2], p[i + 3])) continue;
      const dx = p[i + 2] - p[i], dt = p[i + 3] - p[i + 1]; const L = Math.hypot(dx, dt); if (L < 1e-9) continue;
      let nx = -dt / L, nt = dx / L;
      // keep the side of the ring's centroid (the frontage's side of an alley)
      if ((rs - p[i]) * nx + (rt - p[i + 1]) * nt < 0) { nx = -nx; nt = -nt; }
      ring = clipHalfPlane(ring, p[i] + nx * r.clear, p[i + 1] + nt * r.clear, nx, nt);
      W.clips.push(`(${p[i].toFixed(1)},${p[i + 1].toFixed(1)})→(${p[i + 2].toFixed(1)},${p[i + 3].toFixed(1)}) clear ${r.clear} → ring ${ring.length / 2}`);
    }
  }
  W.ring1 = ring.length / 2; W.ring = ring.map((v) => +v.toFixed(1));
  if (ring.length < 6) { W.why = 'roads empty the parcel'; return null; }
  let tMin = Infinity, tMax = -Infinity;
  for (let k = 1; k < ring.length; k += 2) { if (ring[k] < tMin) tMin = ring[k]; if (ring[k] > tMax) tMax = ring[k]; }
  W.tMin = tMin; W.tMax = tMax;
  const tStart = anchoredFront ? Math.max(tFront, tMin) : tMin;
  if (tMax - tStart < minSide - 1e-9) { W.why = `too shallow: ${(tMax - tStart).toFixed(1)} m`; return null; }
  const step = Math.max(0.5, (tMax - tStart) / 32);
  const cands: Array<{ s0: number; s1: number; t0: number; t1: number; area: number }> = [];
  for (let t0 = tStart; t0 + minSide <= tMax + 1e-9; t0 += step) {
    const c0 = chordAt(ring, Math.min(t0 + 1e-4, tMax), sRef); if (!c0) continue;
    for (let t1 = tMax; t1 - t0 >= minSide - 1e-9; t1 -= step) {
      const c1 = chordAt(ring, Math.max(t1 - 1e-4, tMin), sRef); if (!c1) continue;
      const cm = chordAt(ring, (t0 + t1) / 2, sRef); if (!cm) continue;
      const lo = Math.max(c0[0], c1[0], cm[0]), hi = Math.min(c0[1], c1[1], cm[1]);
      // A hair inside the inset ring: a candidate ON the ring reads 1.5999 against a 1.6 clearance.
      const e = 0.01;
      if (hi - lo - 2 * e < minSide - 1e-9 || t1 - t0 - 2 * e < minSide - 1e-9) continue;
      cands.push({ s0: lo + e, s1: hi - e, t0: t0 + e, t1: t1 - e, area: (hi - lo) * (t1 - t0) });
    }
  }
  W.cands = cands.length;
  if (cands.length === 0) { W.why = 'no chord wide enough'; return null; }
  cands.sort((a, b) => b.area - a.area);
  const top = cands[0].area;
  const order = anchoredFront
    ? [...cands.filter((c) => c.area >= top * 0.8).sort((a, b) => a.t0 - b.t0 || b.area - a.area), ...cands.filter((c) => c.area < top * 0.8)]
    : cands;
  for (const c of order) if (fits(c.s0, c.s1, c.t0, c.t1)) return { s0: c.s0, s1: c.s1, t0: c.t0, t1: c.t1 };
  return null;
}

/**
 * The buildable envelope of a parcel behind its frontage edge (vertex
 * `frontageEdge` → the next): frame s along the frontage, t inward. Returns
 * the rectangle's centre, its width along the frontage, its depth, and the
 * frontage direction as `yaw` (the planner's `orientation`), or null when no
 * `minSide` box fits.
 */
export function lotEnvelope(parcel: ArrayLike<number>, frontageEdge: number, roads: readonly LotRoad[], sideGap: number, minSide: number): LotEnvelope | null {
  const n = parcel.length / 2;
  if (n < 3 || frontageEdge < 0 || frontageEdge >= n) return null;
  const i = frontageEdge, j = (i + 1) % n;
  const ax = parcel[i * 2], az = parcel[i * 2 + 1], bx = parcel[j * 2], bz = parcel[j * 2 + 1];
  const L = Math.hypot(bx - ax, bz - az);
  if (L < 1e-6) return null;
  const ux = (bx - ax) / L, uz = (bz - az) / L;
  const mx = (ax + bx) / 2, mz = (az + bz) / 2;
  let cx = 0, cz = 0;
  for (let k = 0; k < n; k++) { cx += parcel[k * 2]; cz += parcel[k * 2 + 1]; }
  cx /= n; cz /= n;
  let nx = -uz, nz = ux;
  if ((cx - mx) * nx + (cz - mz) * nz < 0) { nx = -nx; nz = -nz; }
  const frame = new Float64Array(n * 2);
  for (let k = 0; k < n; k++) {
    const dx = parcel[k * 2] - mx, dz = parcel[k * 2 + 1] - mz;
    frame[k * 2] = dx * ux + dz * uz; frame[k * 2 + 1] = dx * nx + dz * nz;
  }
  const clear = edgeClearances(parcel, roads, sideGap);
  const r = bestRect(frame, clear, clear[i], minSide, true, frameRoads(parcel, roads, mx, mz, ux, uz, nx, nz), 0);
  if (!r) return null;
  const sc = (r.s0 + r.s1) / 2, tc = (r.t0 + r.t1) / 2;
  return { cx: mx + ux * sc + nx * tc, cz: mz + uz * sc + nz * tc, w: r.s1 - r.s0, d: r.t1 - r.t0, yaw: Math.atan2(uz, ux) };
}

/** The buildable envelope of a parcel with no frontage (a park): the frame follows the polygon's longest edge. */
export function parcelEnvelope(parcel: ArrayLike<number>, roads: readonly LotRoad[], sideGap: number, minSide: number): LotEnvelope | null {
  const n = parcel.length / 2;
  if (n < 3) return null;
  let longest = 0, bestL = -1;
  for (let k = 0; k < n; k++) { const j = (k + 1) % n; const L = Math.hypot(parcel[j * 2] - parcel[k * 2], parcel[j * 2 + 1] - parcel[k * 2 + 1]); if (L > bestL) { bestL = L; longest = k; } }
  const i = longest, j = (i + 1) % n;
  const ax = parcel[i * 2], az = parcel[i * 2 + 1], bx = parcel[j * 2], bz = parcel[j * 2 + 1];
  const L = Math.hypot(bx - ax, bz - az);
  if (L < 1e-6) return null;
  const ux = (bx - ax) / L, uz = (bz - az) / L, nx = -uz, nz = ux;
  const frame = new Float64Array(n * 2);
  for (let k = 0; k < n; k++) { const dx = parcel[k * 2] - ax, dz = parcel[k * 2 + 1] - az; frame[k * 2] = dx * ux + dz * uz; frame[k * 2 + 1] = dx * nx + dz * nz; }
  const clear = edgeClearances(parcel, roads, sideGap);
  let sRef = 0; for (let k = 0; k < n; k++) sRef += frame[k * 2] / n;
  const r = bestRect(frame, clear, 0, minSide, false, frameRoads(parcel, roads, ax, az, ux, uz, nx, nz), sRef);
  if (!r) return null;
  const sc = (r.s0 + r.s1) / 2, tc = (r.t0 + r.t1) / 2;
  return { cx: ax + ux * sc + nx * tc, cz: az + uz * sc + nz * tc, w: r.s1 - r.s0, d: r.t1 - r.t0, yaw: Math.atan2(uz, ux) };
}

/** The planner's four-corner footprint of a rectangle (its corner layout, so every pad reader stays valid). */
export function rectFootprint(cx: number, cz: number, w: number, d: number, yaw: number): Float64Array {
  const hw = w / 2, hd = d / 2, ca = Math.cos(yaw), sa = Math.sin(yaw);
  return Float64Array.from([
    cx + ca * hw - sa * hd, cz + sa * hw + ca * hd,
    cx - ca * hw - sa * hd, cz - sa * hw + ca * hd,
    cx - ca * hw + sa * hd, cz - sa * hw - ca * hd,
    cx + ca * hw + sa * hd, cz + sa * hw - ca * hd,
  ]);
}
